import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Cross, LogOut, Printer, BadgeCheck } from "lucide-react";
import { Reveal } from "../components/Motion";
import { Button } from "../components/ui/button";

function initials(name = "") {
  return name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(undefined);

  useEffect(() => {
    const raw = localStorage.getItem("smct_student");
    setStudent(raw ? JSON.parse(raw) : null);
  }, []);

  if (student === undefined) return null;
  if (student === null) return <Navigate to="/student-login" replace />;

  const logout = () => {
    localStorage.removeItem("smct_student");
    navigate("/student-login");
  };

  const statusCls =
    student.status === "Active"
      ? "bg-forest/15 text-forest"
      : student.status === "Completed"
        ? "bg-gold/25 text-terracotta-dark"
        : "bg-stone-200 text-stone-600";

  return (
    <div data-testid="student-dashboard" className="px-4 pb-24 pt-32 sm:px-6 lg:pt-40">
      <div className="mx-auto max-w-lg">
        <Reveal>
          <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Student Dashboard</p>
          <h1 className="mt-4 text-center font-serif text-4xl font-bold tracking-tight text-forest-deep">
            Welcome, <span className="italic text-terracotta">{student.name?.split(" ")[0]}.</span>
          </h1>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="student-id-card" className="mt-10 overflow-hidden rounded-[2rem] border border-forest/10 bg-white shadow-2xl shadow-forest/10">
            <div className="grain-overlay relative bg-forest-deep px-8 py-7 text-cream">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-forest-deep">
                  <Cross className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-serif text-lg font-bold leading-none">St. Mary's Charitable Trust</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Student Identity Card</p>
                </div>
              </div>
            </div>

            <div className="px-8 py-8">
              <div className="flex items-center gap-5">
                {student.photo ? (
                  <img src={student.photo} alt={student.name} data-testid="student-photo" className="h-24 w-24 rounded-2xl border-2 border-gold/50 object-cover" />
                ) : (
                  <span data-testid="student-photo" className="flex h-24 w-24 items-center justify-center rounded-2xl bg-linen font-serif text-4xl font-bold text-forest">
                    {initials(student.name)}
                  </span>
                )}
                <div>
                  <p data-testid="student-name" className="font-serif text-2xl font-bold text-forest-deep">{student.name}</p>
                  <p data-testid="student-id" className="mt-1 font-mono text-sm text-terracotta-dark">{student.student_id}</p>
                  <span data-testid="student-status" className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusCls}`}>
                    {student.status || "Active"}
                  </span>
                </div>
              </div>

              <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-forest/10 pt-6 text-sm">
                {[
                  ["Course", student.course, "student-course"],
                  ["Batch", student.batch, "student-batch"],
                  ["Mobile", student.phone, "student-mobile"],
                  ["Valid Until", student.valid_until, "student-valid-until"],
                ].map(([label, value, testid]) => (
                  <div key={label}>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">{label}</dt>
                    <dd data-testid={testid} className="mt-1 font-display font-semibold text-forest-deep">{value || "—"}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-8 flex h-10 items-stretch gap-[3px] opacity-70" aria-hidden="true">
                {[3, 1, 2, 1, 4, 1, 2, 3, 1, 2, 1, 4, 2, 1, 3, 1, 2, 1, 4, 1, 2, 3, 1, 2].map((w, i) => (
                  <span key={i} className="bg-forest-deep" style={{ width: `${w * 2}px` }} />
                ))}
                <span className="ml-3 self-center font-mono text-[10px] tracking-[0.3em] text-forest-deep">{student.student_id}</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-8 rounded-2xl border border-forest/10 bg-white p-6" data-testid="student-notice">
            <div className="flex gap-3">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
              <p className="text-sm leading-relaxed text-stone-600">
                Carry a screenshot of this card to class. For certificate collection, corrections or batch changes,
                visit the office or call <a href="tel:+917975094721" className="font-semibold text-forest">+91 79750 94721</a>.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button variant="outline" onClick={() => window.print()} data-testid="student-print-button" className="rounded-full border-forest/30 font-display text-forest hover:bg-forest hover:text-cream">
              <Printer className="mr-2 h-4 w-4" /> Print ID Card
            </Button>
            <Button variant="outline" onClick={logout} data-testid="student-logout-button" className="rounded-full border-forest/30 font-display text-forest hover:bg-forest hover:text-cream">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
